/**
 * dictation_controller.js — tap-to-record voice dictation for editable fields.
 *
 * Ports static/js/whisper-dictation.js to an element-scoped controller, one
 * instance per mic button ([data-action="whisper-record"] [data-target="#field"],
 * data-controller="dictation"). First tap starts MediaRecorder, second stops +
 * uploads to /quotes/transcribe; the transcript is inserted at the cursor captured
 * on mousedown (before focus leaves the field).
 *
 * Lifecycle: connect() binds mousedown (cursor capture) + click (toggle) on the
 * button and a connect-scoped turbo:before-cache that stops any in-flight
 * recording and resets the button — replacing the old document-delegated handlers
 * and the manual before-cache loop. disconnect() does the same teardown so a
 * recording can't outlive the page. The per-button recording session lives in a
 * module WeakMap keyed by the button element (one active recording per button).
 *
 * Premium-gated server-side. Targets: <textarea>, <input>, contenteditable.
 *
 * Once a transcript has finished typing into a <textarea> or <input>, the
 * button dispatches a bubbling `dictation:transcribed` event, so a parent can
 * act on the words as soon as they are in the field (chip_fill_controller.js
 * fills its form from them). Contenteditable targets do not announce.
 */

import { Controller } from "@hotwired/stimulus";

    var STATE = { idle: 'idle', recording: 'recording', uploading: 'uploading' };
    var sessions = new WeakMap();
    var WAVE_BARS = 24;

    function formatElapsed(ms) {
        var total = Math.floor(ms / 1000);
        var m = Math.floor(total / 60);
        var s = total % 60;
        return m + ':' + (s < 10 ? '0' : '') + s;
    }

    function ensureTimerEl(btn) {
        var id = btn.dataset.whisperTimerId;
        if (id) {
            var existing = document.getElementById(id);
            if (existing) return existing;
        }
        id = 'whisperTimer_' + Math.random().toString(36).slice(2, 8);
        btn.dataset.whisperTimerId = id;
        var timer = document.createElement('span');
        timer.id = id;
        timer.className = 'badge bg-danger position-absolute';
        timer.style.cssText = 'top: 0.5rem; right: 3rem; display: none; font-variant-numeric: tabular-nums;';
        timer.textContent = '0:00';
        btn.parentNode.insertBefore(timer, btn);
        return timer;
    }

    function startTimer(btn) {
        var timer = ensureTimerEl(btn);
        var start = Date.now();
        timer.textContent = '0:00';
        timer.style.display = '';
        var intervalId = setInterval(function () {
            timer.textContent = formatElapsed(Date.now() - start);
        }, 250);
        return { timer: timer, intervalId: intervalId };
    }

    function stopTimer(timerHandle) {
        if (!timerHandle) return;
        clearInterval(timerHandle.intervalId);
        if (timerHandle.timer) timerHandle.timer.style.display = 'none';
    }

    // --- Live waveform: voice-memo-style scrolling level bars from the same
    // MediaStream the recorder consumes. The AnalyserNode is a read-only probe. ---
    function ensureWaveformEl(btn) {
        var id = btn.dataset.whisperWaveformId;
        if (id) {
            var existing = document.getElementById(id);
            if (existing) return existing;
        }
        id = 'whisperWave_' + Math.random().toString(36).slice(2, 8);
        btn.dataset.whisperWaveformId = id;
        var canvas = document.createElement('canvas');
        canvas.id = id;
        canvas.width = 128;   // 2x internal resolution for retina crispness
        canvas.height = 48;
        canvas.style.cssText = 'position: absolute; top: 0.5rem; right: 6.5rem; ' +
            'width: 64px; height: 24px; display: none;';
        btn.parentNode.insertBefore(canvas, btn);
        return canvas;
    }

    function startWaveform(btn, stream) {
        var Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return null;  // graceful: timer still shows without the bars

        var canvas = ensureWaveformEl(btn);
        var ctx2d = canvas.getContext('2d');
        var audioCtx = new Ctx();
        if (audioCtx.resume) audioCtx.resume();
        var analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;  // 128 frequency bins
        audioCtx.createMediaStreamSource(stream).connect(analyser);
        var freq = new Uint8Array(analyser.frequencyBinCount);
        var levels = new Array(WAVE_BARS).fill(0);

        canvas.style.display = '';
        var handle = { canvas: canvas, audioCtx: audioCtx, rafId: 0 };

        function draw() {
            analyser.getByteFrequencyData(freq);
            var sum = 0;
            for (var i = 0; i < freq.length; i++) sum += freq[i];
            var avg = sum / freq.length / 255;          // 0..1
            levels.push(Math.min(1, avg * 3));          // scale up sparse voice spectrum
            levels.shift();                             // scroll left

            ctx2d.clearRect(0, 0, canvas.width, canvas.height);
            ctx2d.fillStyle = '#dc3545';
            var bw = canvas.width / WAVE_BARS;
            for (var j = 0; j < WAVE_BARS; j++) {
                var h = Math.max(2, levels[j] * canvas.height);
                ctx2d.fillRect(j * bw + 1, (canvas.height - h) / 2, bw - 2, h);
            }
            handle.rafId = requestAnimationFrame(draw);
        }
        handle.rafId = requestAnimationFrame(draw);
        return handle;
    }

    function stopWaveform(waveHandle) {
        if (!waveHandle) return;
        cancelAnimationFrame(waveHandle.rafId);
        if (waveHandle.canvas) waveHandle.canvas.style.display = 'none';
        if (waveHandle.audioCtx && waveHandle.audioCtx.state !== 'closed') {
            waveHandle.audioCtx.close();
        }
    }

    function setButtonState(btn, state, message) {
        btn.dataset.whisperState = state;
        var icon = btn.querySelector('i');
        if (state === STATE.recording) {
            btn.classList.add('btn-danger');
            btn.classList.remove('btn-outline-secondary');
            btn.classList.remove('whisper-uploading');
            btn.title = 'Stop recording';
            if (icon) icon.className = 'bi bi-stop-circle-fill';
        } else if (state === STATE.uploading) {
            btn.classList.remove('btn-danger');
            btn.classList.add('btn-outline-secondary');
            btn.disabled = true;
            btn.title = 'Transcribing…';
            if (icon) icon.className = 'bi bi-arrow-repeat whisper-spin';
        } else {
            btn.classList.remove('btn-danger');
            btn.classList.add('btn-outline-secondary');
            btn.disabled = false;
            btn.title = message || 'Dictate';
            if (icon) icon.className = 'bi bi-mic';
        }
    }

    function animateIntoSpan(span, text, target) {
        var i = 0;
        var intervalId = setInterval(function () {
            i = Math.min(i + 2, text.length);
            span.textContent = text.slice(0, i);
            target.scrollTop = target.scrollHeight;
            if (i >= text.length) {
                clearInterval(intervalId);
                target.dispatchEvent(new Event('input', { bubbles: true }));
            }
        }, 15);
    }

    function insertAtTarget(btn, targetSelector, text) {
        var target = document.querySelector(targetSelector);
        if (!target) return;

        // Editors that own their DOM opt out of direct mutation by exposing this
        // hook. ProseMirror (the rich description editor) watches its element
        // with a MutationObserver and re-parses anything it did not author, so
        // the animated span below gets detached mid-transcript and the text is
        // silently lost. The hook routes through the editor's own insert
        // command, which also restores the cursor the user left behind.
        if (typeof target.chippieInsertText === 'function') {
            btn._whisperRange = null;
            target.chippieInsertText(text);
            return;
        }

        if (target.isContentEditable) {
            insertIntoContentEditable(target, text, btn._whisperRange);
            btn._whisperRange = null;
            return;
        }

        var fullText = target.value || '';
        var start  = btn._whisperCursorStart != null ? btn._whisperCursorStart : fullText.length;
        var end    = btn._whisperCursorEnd   != null ? btn._whisperCursorEnd   : fullText.length;
        btn._whisperCursorStart = btn._whisperCursorEnd = null;

        var before = fullText.slice(0, start);
        var after  = fullText.slice(end);
        if (before.length && !/\s$/.test(before)) before += ' ';
        if (after.length  && !/^\s/.test(after))  after   = ' ' + after;

        var i = 0;
        var intervalId = setInterval(function () {
            i = Math.min(i + 2, text.length);
            target.value = before + text.slice(0, i) + after;
            target.selectionStart = target.selectionEnd = before.length + i;
            target.scrollTop = target.scrollHeight;
            if (i >= text.length) {
                clearInterval(intervalId);
                target.dispatchEvent(new Event('input', { bubbles: true }));
                btn.dispatchEvent(new CustomEvent('dictation:transcribed', { bubbles: true }));
            }
        }, 15);
    }

    function insertIntoContentEditable(target, text, savedRange) {
        var span = document.createElement('span');

        // Guard: fall back to end-append if we have no usable cursor.
        if (!savedRange || !target.contains(savedRange.startContainer)) {
            if ((target.textContent || '').trim()) target.appendChild(document.createTextNode(' '));
            target.appendChild(span);
            animateIntoSpan(span, text, target);
            return;
        }

        savedRange.deleteContents();

        var preRange = document.createRange();
        preRange.setStart(target, 0);
        preRange.setEnd(savedRange.startContainer, savedRange.startOffset);
        if (/\S$/.test(preRange.toString())) {
            savedRange.insertNode(document.createTextNode(' '));
            savedRange.collapse(false);
        }

        savedRange.insertNode(span);

        var next = span.nextSibling;
        if (next && next.nodeType === Node.TEXT_NODE && /^\S/.test(next.data)) {
            span.parentNode.insertBefore(document.createTextNode(' '), next);
        }

        animateIntoSpan(span, text, target);
    }

    function showError(btn, msg) {
        setButtonState(btn, STATE.idle);
        if (window.showToast) {
            window.showToast(msg, 'error');
        } else {
            alert(msg);
        }
    }

    function isPremium() {
        var meta = document.querySelector('meta[name="current-plan-id"]');
        var plan = meta ? (meta.getAttribute('content') || '').toLowerCase() : 'free';
        return plan && plan !== 'free';
    }

    // Premium-feature upgrade card, rendered inline next to the mic button.
    function showUpgradePrompt(btn) {
        setButtonState(btn, STATE.idle);
        var anchor = btn.parentNode || document.body;
        var existing = anchor.querySelector(':scope > .whisper-upgrade-prompt');
        if (existing) return;
        var card = document.createElement('div');
        card.className = 'whisper-upgrade-prompt';
        card.style.cssText = 'background: #eaf3ff; border: 1px solid #b6d4fe; ' +
            'border-left: 4px solid #0d6efd; border-radius: 6px; padding: 0.75rem; ' +
            'margin-top: 0.5rem; position: relative;';
        // App Store 3.1.1 / Google Play Payments: no purchase surface inside the
        // native shells. The card keeps saying WHY dictation is locked and drops
        // the "upgrade" wording and the /billing/plans link, matching the
        // server-rendered gates (the seat cap on templates/team/invite_member.html
        // and friends). Those read Jinja's is_native_shell; markup built here has
        // no server context, so read the twin flag base.html stamps on <html>
        // (the same test maps-navigation.js and pwa-install.js use).
        var isNativeShell = document.documentElement.hasAttribute('data-hotwire-native');
        card.innerHTML =
            '<button type="button" class="btn-close" aria-label="Close" ' +
                'style="position:absolute; top:0.5rem; right:0.5rem;"></button>' +
            '<div style="font-weight:600; color:#0a58ca; margin-bottom:0.25rem;">' +
                '<i class="bi bi-stars me-1"></i>Premium feature' +
            '</div>' +
            '<div style="color:#0a3a78; font-size:0.92em; margin-bottom:0.5rem;">' +
                'Voice dictation is included with an active subscription.' +
                (isNativeShell ? '' : ' Upgrade to dictate into any field.') +
            '</div>' +
            (isNativeShell ? '' :
                '<a href="/billing/plans" class="btn btn-sm btn-primary">' +
                    '<i class="bi bi-arrow-up-right-circle me-1"></i>View plans' +
                '</a>');
        anchor.appendChild(card);
        var dismiss = function () { if (card.parentElement) card.remove(); };
        card.querySelector('.btn-close').addEventListener('click', dismiss);
        setTimeout(dismiss, 10000);
    }

    async function startRecording(btn) {
        if (!navigator.mediaDevices || !window.MediaRecorder) {
            showError(btn, 'Voice dictation not supported in this browser');
            return;
        }
        try {
            var stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            var recorder = new MediaRecorder(stream);
            var chunks = [];
            recorder.addEventListener('dataavailable', function (e) {
                if (e.data && e.data.size > 0) chunks.push(e.data);
            });
            recorder.addEventListener('stop', function () {
                stream.getTracks().forEach(function (t) { t.stop(); });
                uploadAudio(btn, chunks, recorder.mimeType);
            });
            recorder.start();
            var timerHandle = startTimer(btn);
            var waveHandle = startWaveform(btn, stream);
            sessions.set(btn, {
                recorder: recorder, stream: stream,
                timerHandle: timerHandle, waveHandle: waveHandle
            });
            setButtonState(btn, STATE.recording);
        } catch (_err) {
            showError(btn, 'Microphone access denied');
        }
    }

    function stopRecording(btn) {
        var session = sessions.get(btn);
        if (!session) return;
        stopTimer(session.timerHandle);
        stopWaveform(session.waveHandle);
        setButtonState(btn, STATE.uploading);
        try {
            session.recorder.stop();
        } catch (_err) {
            // recorder already stopped — safe to ignore
        }
        sessions.delete(btn);
    }

    function setTargetTranscribing(targetSelector, on) {
        var target = document.querySelector(targetSelector);
        if (!target) return;
        var wrapper = target.parentNode;
        if (!wrapper) return;
        target.classList.toggle('whisper-transcribing', !!on);

        var existingShimmer = wrapper.querySelector('.whisper-overlay-shimmer');
        var existingLabel = wrapper.querySelector('.whisper-overlay');
        if (on) {
            if (!existingShimmer) {
                var shim = document.createElement('div');
                shim.className = 'whisper-overlay-shimmer';
                wrapper.appendChild(shim);
            }
            var existingText = target.isContentEditable
                ? (target.textContent || '')
                : (target.value || '');
            if (!existingLabel && !existingText.trim()) {
                if ('placeholder' in target && target.placeholder) {
                    target.dataset.whisperOriginalPlaceholder = target.placeholder;
                    target.placeholder = '';
                }
                var label = document.createElement('div');
                label.className = 'whisper-overlay';
                label.innerHTML = 'Transcribing<span class="whisper-dots"><span></span><span></span><span></span></span>';
                wrapper.appendChild(label);
            }
        } else {
            if (existingShimmer) existingShimmer.remove();
            if (existingLabel) existingLabel.remove();
            if ('placeholder' in target && target.dataset.whisperOriginalPlaceholder !== undefined) {
                target.placeholder = target.dataset.whisperOriginalPlaceholder;
                delete target.dataset.whisperOriginalPlaceholder;
            }
        }
    }

    function uploadAudio(btn, chunks, mimeType) {
        var blob = new Blob(chunks, { type: mimeType || 'audio/webm' });
        var form = new FormData();
        form.append('audio', blob, 'dictation.webm');
        setTargetTranscribing(btn.dataset.target, true);
        fetch('/quotes/transcribe', { method: 'POST', body: form, credentials: 'same-origin' })
            .then(function (res) {
                return res.json().then(function (json) { return { ok: res.ok, status: res.status, json: json }; });
            })
            .then(function (result) {
                setTargetTranscribing(btn.dataset.target, false);
                if (result.ok && result.json.success) {
                    insertAtTarget(btn, btn.dataset.target, result.json.text);
                    setButtonState(btn, STATE.idle);
                    return;
                }
                if (result.status === 402) {
                    showUpgradePrompt(btn);
                } else {
                    showError(btn, result.json.error || 'Transcription failed');
                }
            })
            .catch(function () {
                setTargetTranscribing(btn.dataset.target, false);
                showError(btn, 'Network error during transcription');
            });
    }

export default class extends Controller {
        connect() {
            this._onMousedown = this.captureCursor.bind(this);
            this._onClick = this.toggle.bind(this);
            this.element.addEventListener('mousedown', this._onMousedown);
            this.element.addEventListener('click', this._onClick);

            this._onBeforeCache = this.teardown.bind(this);
            document.addEventListener('turbo:before-cache', this._onBeforeCache);
        }

        disconnect() {
            this.element.removeEventListener('mousedown', this._onMousedown);
            this.element.removeEventListener('click', this._onClick);
            if (this._onBeforeCache) {
                document.removeEventListener('turbo:before-cache', this._onBeforeCache);
                this._onBeforeCache = null;
            }
            this.teardown();
        }

        // Capture cursor position before the button click moves focus away from
        // the target field. mousedown fires before blur on the previously-focused
        // element, so selectionStart/End and the contenteditable Range are valid here.
        captureCursor() {
            var btn = this.element;
            if ((btn.dataset.whisperState || STATE.idle) !== STATE.idle) return;
            var targetSelector = btn.dataset.target;
            if (!targetSelector) return;
            var target = document.querySelector(targetSelector);
            if (!target) return;

            if (target.isContentEditable) {
                var sel = window.getSelection();
                btn._whisperRange = (sel && sel.rangeCount > 0 && target.contains(sel.anchorNode))
                    ? sel.getRangeAt(0).cloneRange()
                    : null;
            } else {
                btn._whisperCursorStart = (typeof target.selectionStart === 'number') ? target.selectionStart : null;
                btn._whisperCursorEnd   = (typeof target.selectionEnd   === 'number') ? target.selectionEnd   : null;
            }
        }

        toggle(event) {
            event.preventDefault();
            var btn = this.element;
            var state = btn.dataset.whisperState || STATE.idle;
            if (state === STATE.idle) {
                if (!isPremium()) {
                    showUpgradePrompt(btn);
                    return;
                }
                startRecording(btn);
            } else if (state === STATE.recording) {
                stopRecording(btn);
            }
        }

        // Stop any in-flight recording and reset the button before a snapshot or
        // when the element leaves the page.
        teardown() {
            var btn = this.element;
            var session = sessions.get(btn);
            if (session) {
                try { session.recorder.stop(); } catch (_e) { /* noop */ }
                session.stream.getTracks().forEach(function (t) { t.stop(); });
                stopTimer(session.timerHandle);
                stopWaveform(session.waveHandle);
                sessions.delete(btn);
            }
            setButtonState(btn, STATE.idle);
        }
    }
